import { DataStore, OpType } from "@aws-amplify/datastore";
import { ModuleLog, getModuleLog } from "../../Logger";
import fluent, { FluentIterable } from "fluent-iterable";
import { Change, ChangeSet } from "../../DataStore/ChangeSet";
import { DataProvider, DataProviderArgs } from "../../DataStore/DataProvider";
import deepDiff from "deep-diff";
import cloneDeep from "lodash-es/cloneDeep.js";

export abstract class DataStoreTable<T extends { id: string | undefined }> extends DataProvider {
    private _all = new Map<string, T>();
    protected _log: ModuleLog;
    protected _count = 0;

    constructor(
        args: DataProviderArgs,
        protected readonly _modelName: string,
        protected readonly _model: any,
        protected readonly _toWrapper: (obj: any) => any = (obj: any) => cloneDeep(obj),
        protected readonly _fromWrapper: (obj: any) => any = (obj: any) => cloneDeep(obj)
    ) {
        super(args, []);
        this._log = getModuleLog(this._modelName);

        DataStore.observe(this._model).subscribe((msg: any) =>
            this._onChange(msg.element, msg.opType)
        );

        // Initial query will retrieve all local items
        DataStore.query(this._model).then((items: any) => {
            items.forEach((x: any) => this._all.set(x.id as string, this._toWrapper(x)));
            this._count = items.length;
            this._onInitialQuery();
        });
    }

    count(): number {
        return this._count;
    }

    override async all(): Promise<FluentIterable<T>> {
        return this.allSync();
    }

    allSync(): FluentIterable<T> {
        return fluent(this._all.values());
    }

    override async allOwnedBy(id: string): Promise<FluentIterable<T>> {
        return this.allOwnedBySync(id);
    }

    allOwnedBySync(id: string): FluentIterable<T> {
        return this.allSync().filter((x: T) => (x as any).owner === id);
    }

    override async read(id: string): Promise<T | undefined> {
        return this._all.get(id);
    }

    override async create(obj: any): Promise<T> {
        const amplifyObj = this._fromWrapper(obj) as any;

        // Optimistically update
        this._onChange(amplifyObj, OpType.INSERT);

        return await this._toWrapper(await DataStore.save(amplifyObj));
    }

    public async upsert(obj: T): Promise<T | undefined> {
        if (!obj.id || obj.id.length === 0) {
            return await this.create(obj);
        } else {
            return await this.update(obj);
        }
    }

    override async delete(id: string): Promise<void> {
        const toDelete = await DataStore.query(this._model, id);
        if (toDelete) {
            // Optimistically update
            this._onChange(toDelete, OpType.DELETE);
            await DataStore.delete(toDelete);
        }
    }

    override async update(item: any): Promise<T | undefined> {
        this._optimisticUpdateItem(item);

        const existing = await DataStore.query(this._model, item.id);
        if (existing) {
            const amplifyObj = this._fromWrapper(item);
            const newItem = this._model.copyOf(existing, (updated: any) => {
                for (const prop in item) {
                    if (prop !== "id") {
                        updated[prop] = amplifyObj[prop];
                    }
                }
            });

            // Do the actual update. This will raise an event on completion
            return this._toWrapper(await DataStore.save(newItem));
        }
        return undefined;
    }

    protected _optimisticUpdateItem(item: any) {
        const curItem = cloneDeep(this.read(item.id));
        if (curItem) {
            Object.assign(curItem, item);
            this._onChange(curItem, OpType.UPDATE);
        }
    }

    protected async _onInitialQuery() {
        const all = await this.all();
        this._notifyListeners(new ChangeSet(all.toArray(), [], []));
    }

    protected _onChange(amplifyItem: any, opType: OpType) {
        const changeSet = new ChangeSet();
        const item = this._toWrapper(amplifyItem);
        const softDeleted = (item as any).deleted ?? false;
        if (softDeleted) {
            opType = OpType.DELETE;
        }

        switch (opType) {
            case OpType.INSERT:
                if (!this._all.has(item.id)) {
                    this._all.set(item.id, item);
                    ++this._count;
                    changeSet.created.push(item);
                }
                break;
            case OpType.UPDATE:
                {
                    const current = this._all.get(item.id);
                    this._all.delete(item.id);
                    this._all.set(item.id, item);

                    const diff = deepDiff(current, item);
                    if (diff && diff.length > 0) {
                        changeSet.updated.push(new Change(diff, current, item));
                    }
                }
                break;
            case OpType.DELETE:
                if (this._all.has(item.id)) {
                    this._all.delete(item.id);
                    --this._count;
                    changeSet.deleted.push(item);
                }
                break;
        }
        this._notifyListeners(changeSet);
    }
}
